import React from "react";
import { useEditor } from "@craftjs/core";
import {
  MdDeleteOutline,
  MdLayers,
  MdTune,
  MdColorLens,
  MdTextFields,
  MdOutlineImage,
  MdPhone,
  MdStar,
  MdShoppingBag,
  MdLocalOffer,
} from "react-icons/md";

export const CraftSettingsPanel = () => {
  const { selected, isRoot, actions } = useEditor((state) => {
    const [currentNodeId] = state.events.selected;
    let selected;

    if (currentNodeId) {
      selected = {
        id: currentNodeId,
        name: state.nodes[currentNodeId].data.displayName,
        settings:
          state.nodes[currentNodeId].related &&
          state.nodes[currentNodeId].related.settings,
        isDeletable: state.nodes[currentNodeId].data.isDeletable,
        props: state.nodes[currentNodeId].data.props,
      };
    }

    return {
      selected,
      isRoot: currentNodeId === "ROOT",
    };
  });

  if (!selected) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6 text-center text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 select-none">
        <div className="w-14 h-14 rounded-2xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-500 flex items-center justify-center mb-3 shadow-inner">
          <MdTune size={26} />
        </div>
        <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
          No Component Selected
        </h4>
        <p className="text-xs max-w-[200px] leading-relaxed">
          Click any block on the canvas to edit text inline or customize its colors and settings.
        </p>
      </div>
    );
  }

  const props = selected.props || {};

  const handlePropChange = (key, value) => {
    actions.setProp(selected.id, (nodeProps) => {
      nodeProps[key] = value;
    });
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 select-none">
      {/* Panel Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2.5 h-2.5 rounded-full bg-indigo-600 animate-pulse" />
          <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider truncate">
            {selected.name || "Block Settings"}
          </h3>
        </div>

        {!isRoot && (
          <button
            type="button"
            onClick={() => actions.delete(selected.id)}
            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition-colors"
            title="Delete block"
          >
            <MdDeleteOutline size={18} />
          </button>
        )}
      </div>

      {/* Settings Form Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5 text-xs text-gray-700 dark:text-gray-300">
        {/* TEXT & HEADINGS */}
        {(props.text !== undefined ||
          props.title !== undefined ||
          props.heading !== undefined ||
          props.sectionTitle !== undefined ||
          props.storeName !== undefined ||
          props.eyebrow !== undefined) && (
          <div className="space-y-3">
            <label className="flex items-center gap-1.5 font-bold text-gray-900 dark:text-gray-100 uppercase text-[10px] tracking-wider">
              <MdTextFields size={14} className="text-indigo-500" />
              <span>Text & Headings</span>
            </label>

            {props.eyebrow !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Eyebrow Tag</span>
                <input
                  type="text"
                  value={props.eyebrow}
                  onChange={(e) => handlePropChange("eyebrow", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.text !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Text Content</span>
                <textarea
                  rows={3}
                  value={props.text}
                  onChange={(e) => handlePropChange("text", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.title !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Title</span>
                <input
                  type="text"
                  value={props.title}
                  onChange={(e) => handlePropChange("title", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.heading !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Heading</span>
                <input
                  type="text"
                  value={props.heading}
                  onChange={(e) => handlePropChange("heading", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.sectionTitle !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Section Title</span>
                <input
                  type="text"
                  value={props.sectionTitle}
                  onChange={(e) => handlePropChange("sectionTitle", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.storeName !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Store Name</span>
                <input
                  type="text"
                  value={props.storeName}
                  onChange={(e) => handlePropChange("storeName", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.subtitle !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Subtitle</span>
                <textarea
                  rows={2}
                  value={props.subtitle}
                  onChange={(e) => handlePropChange("subtitle", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.subheading !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Subheading</span>
                <textarea
                  rows={2}
                  value={props.subheading}
                  onChange={(e) => handlePropChange("subheading", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.sectionSubtitle !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Section Subtitle</span>
                <textarea
                  rows={2}
                  value={props.sectionSubtitle}
                  onChange={(e) => handlePropChange("sectionSubtitle", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.tagline !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Store Tagline</span>
                <textarea
                  rows={2}
                  value={props.tagline}
                  onChange={(e) => handlePropChange("tagline", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.copyright !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Copyright Notice</span>
                <input
                  type="text"
                  value={props.copyright}
                  onChange={(e) => handlePropChange("copyright", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.fontSize !== undefined && (
              <div>
                <div className="flex justify-between text-[11px] text-gray-500 mb-1">
                  <span>Font Size</span>
                  <span>{props.fontSize}px</span>
                </div>
                <input
                  type="range"
                  min={12}
                  max={64}
                  value={props.fontSize}
                  onChange={(e) => handlePropChange("fontSize", parseInt(e.target.value, 10))}
                  className="w-full accent-indigo-600"
                />
              </div>
            )}
          </div>
        )}

        {/* FEATURES LIST PROPERTIES */}
        {props.feature1Title !== undefined && (
          <div className="space-y-3 pt-3 border-t border-gray-100 dark:border-gray-800">
            <label className="flex items-center gap-1.5 font-bold text-gray-900 dark:text-gray-100 uppercase text-[10px] tracking-wider">
              <MdTune size={14} className="text-indigo-500" />
              <span>Feature Highlights</span>
            </label>

            <div className="space-y-2">
              <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-400">Feature 1</span>
              <input
                type="text"
                placeholder="Title"
                value={props.feature1Title}
                onChange={(e) => handlePropChange("feature1Title", e.target.value)}
                className="w-full px-3 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <input
                type="text"
                placeholder="Description"
                value={props.feature1Desc}
                onChange={(e) => handlePropChange("feature1Desc", e.target.value)}
                className="w-full px-3 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="space-y-2 pt-2">
              <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-400">Feature 2</span>
              <input
                type="text"
                placeholder="Title"
                value={props.feature2Title}
                onChange={(e) => handlePropChange("feature2Title", e.target.value)}
                className="w-full px-3 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <input
                type="text"
                placeholder="Description"
                value={props.feature2Desc}
                onChange={(e) => handlePropChange("feature2Desc", e.target.value)}
                className="w-full px-3 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="space-y-2 pt-2">
              <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-400">Feature 3</span>
              <input
                type="text"
                placeholder="Title"
                value={props.feature3Title}
                onChange={(e) => handlePropChange("feature3Title", e.target.value)}
                className="w-full px-3 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <input
                type="text"
                placeholder="Description"
                value={props.feature3Desc}
                onChange={(e) => handlePropChange("feature3Desc", e.target.value)}
                className="w-full px-3 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="space-y-2 pt-2">
              <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-400">Feature 4</span>
              <input
                type="text"
                placeholder="Title"
                value={props.feature4Title}
                onChange={(e) => handlePropChange("feature4Title", e.target.value)}
                className="w-full px-3 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <input
                type="text"
                placeholder="Description"
                value={props.feature4Desc}
                onChange={(e) => handlePropChange("feature4Desc", e.target.value)}
                className="w-full px-3 py-1.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        )}

        {/* CONTACT INFO PROPERTIES */}
        {(props.phone !== undefined || props.email !== undefined || props.address !== undefined) && (
          <div className="space-y-3 pt-3 border-t border-gray-100 dark:border-gray-800">
            <label className="flex items-center gap-1.5 font-bold text-gray-900 dark:text-gray-100 uppercase text-[10px] tracking-wider">
              <MdPhone size={14} className="text-indigo-500" />
              <span>Contact Information</span>
            </label>

            {props.phone !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Phone Number</span>
                <input
                  type="text"
                  value={props.phone}
                  onChange={(e) => handlePropChange("phone", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.email !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Email Address</span>
                <input
                  type="text"
                  value={props.email}
                  onChange={(e) => handlePropChange("email", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.address !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Physical Address</span>
                <textarea
                  rows={2}
                  value={props.address}
                  onChange={(e) => handlePropChange("address", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.whatsappButtonText !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">WhatsApp Button Label</span>
                <input
                  type="text"
                  value={props.whatsappButtonText}
                  onChange={(e) => handlePropChange("whatsappButtonText", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}
          </div>
        )}

        {/* BUTTON PROPERTIES */}
        {(props.primaryButtonText !== undefined ||
          props.secondaryButtonText !== undefined ||
          props.buttonText !== undefined ||
          selected.name === "Action Button") && (
          <div className="space-y-3 pt-3 border-t border-gray-100 dark:border-gray-800">
            <label className="flex items-center gap-1.5 font-bold text-gray-900 dark:text-gray-100 uppercase text-[10px] tracking-wider">
              <MdTune size={14} className="text-indigo-500" />
              <span>Button Controls</span>
            </label>

            {props.primaryButtonText !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Primary Button Label</span>
                <input
                  type="text"
                  value={props.primaryButtonText}
                  onChange={(e) => handlePropChange("primaryButtonText", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.secondaryButtonText !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Secondary Button Label</span>
                <input
                  type="text"
                  value={props.secondaryButtonText}
                  onChange={(e) => handlePropChange("secondaryButtonText", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.buttonText !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Button Label</span>
                <input
                  type="text"
                  value={props.buttonText}
                  onChange={(e) => handlePropChange("buttonText", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.link !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Target URL / Link</span>
                <input
                  type="text"
                  value={props.link}
                  onChange={(e) => handlePropChange("link", e.target.value)}
                  placeholder="# or /products"
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}
          </div>
        )}

        {/* PROMO / BADGE / COUPON */}
        {(props.badge !== undefined || props.couponCode !== undefined) && (
          <div className="space-y-3 pt-3 border-t border-gray-100 dark:border-gray-800">
            <label className="flex items-center gap-1.5 font-bold text-gray-900 dark:text-gray-100 uppercase text-[10px] tracking-wider">
              <MdLocalOffer size={14} className="text-indigo-500" />
              <span>Badge & Coupon</span>
            </label>

            {props.badge !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Badge Tag</span>
                <input
                  type="text"
                  value={props.badge}
                  onChange={(e) => handlePropChange("badge", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.couponCode !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Coupon Code</span>
                <input
                  type="text"
                  value={props.couponCode}
                  onChange={(e) => handlePropChange("couponCode", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500 uppercase font-mono"
                />
              </div>
            )}
          </div>
        )}

        {/* PRODUCT SHOWCASE SETTINGS */}
        {props.columns !== undefined && (
          <div className="space-y-3 pt-3 border-t border-gray-100 dark:border-gray-800">
            <label className="flex items-center gap-1.5 font-bold text-gray-900 dark:text-gray-100 uppercase text-[10px] tracking-wider">
              <MdShoppingBag size={14} className="text-indigo-500" />
              <span>Product Grid Layout</span>
            </label>

            <div>
              <div className="flex justify-between text-[11px] text-gray-500 mb-1">
                <span>Columns</span>
                <span>{props.columns} items</span>
              </div>
              <input
                type="range"
                min={2}
                max={4}
                step={1}
                value={props.columns}
                onChange={(e) => handlePropChange("columns", parseInt(e.target.value, 10))}
                className="w-full accent-indigo-600"
              />
            </div>

            {props.showRating !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-gray-500">Show Ratings</span>
                <input
                  type="checkbox"
                  checked={props.showRating}
                  onChange={(e) => handlePropChange("showRating", e.target.checked)}
                  className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.showBadge !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-gray-500">Show Badges</span>
                <input
                  type="checkbox"
                  checked={props.showBadge}
                  onChange={(e) => handlePropChange("showBadge", e.target.checked)}
                  className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                />
              </div>
            )}
          </div>
        )}

        {/* COLORS & STYLING */}
        <div className="space-y-3 pt-3 border-t border-gray-100 dark:border-gray-800">
          <label className="flex items-center gap-1.5 font-bold text-gray-900 dark:text-gray-100 uppercase text-[10px] tracking-wider">
            <MdColorLens size={14} className="text-indigo-500" />
            <span>Colors & Theme</span>
          </label>

          {props.primaryColor !== undefined && (
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-gray-500">Primary Brand Color</span>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={props.primaryColor}
                  onChange={(e) => handlePropChange("primaryColor", e.target.value)}
                  className="w-7 h-7 rounded border border-gray-300 cursor-pointer"
                />
                <span className="font-mono text-[11px]">{props.primaryColor}</span>
              </div>
            </div>
          )}

          {props.backgroundColor !== undefined && (
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-gray-500">Background Color</span>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={props.backgroundColor}
                  onChange={(e) => handlePropChange("backgroundColor", e.target.value)}
                  className="w-7 h-7 rounded border border-gray-300 cursor-pointer"
                />
                <span className="font-mono text-[11px]">{props.backgroundColor}</span>
              </div>
            </div>
          )}

          {props.background !== undefined && (
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-gray-500">Section Background</span>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={props.background}
                  onChange={(e) => handlePropChange("background", e.target.value)}
                  className="w-7 h-7 rounded border border-gray-300 cursor-pointer"
                />
                <span className="font-mono text-[11px]">{props.background}</span>
              </div>
            </div>
          )}

          {props.cardBackground !== undefined && (
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-gray-500">Card Background</span>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={props.cardBackground}
                  onChange={(e) => handlePropChange("cardBackground", e.target.value)}
                  className="w-7 h-7 rounded border border-gray-300 cursor-pointer"
                />
                <span className="font-mono text-[11px]">{props.cardBackground}</span>
              </div>
            </div>
          )}

          {props.color !== undefined && (
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-gray-500">Text Color</span>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={props.color}
                  onChange={(e) => handlePropChange("color", e.target.value)}
                  className="w-7 h-7 rounded border border-gray-300 cursor-pointer"
                />
                <span className="font-mono text-[11px]">{props.color}</span>
              </div>
            </div>
          )}

          {props.iconColor !== undefined && (
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-gray-500">Icon Color</span>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={props.iconColor}
                  onChange={(e) => handlePropChange("iconColor", e.target.value)}
                  className="w-7 h-7 rounded border border-gray-300 cursor-pointer"
                />
                <span className="font-mono text-[11px]">{props.iconColor}</span>
              </div>
            </div>
          )}
        </div>

        {/* IMAGE / MEDIA CONTROLS */}
        {(props.src !== undefined || props.bgImage !== undefined) && (
          <div className="space-y-3 pt-3 border-t border-gray-100 dark:border-gray-800">
            <label className="flex items-center gap-1.5 font-bold text-gray-900 dark:text-gray-100 uppercase text-[10px] tracking-wider">
              <MdOutlineImage size={14} className="text-indigo-500" />
              <span>Image & Media</span>
            </label>

            {props.src !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Image URL</span>
                <input
                  type="text"
                  value={props.src}
                  onChange={(e) => handlePropChange("src", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.bgImage !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Background Image URL</span>
                <input
                  type="text"
                  value={props.bgImage}
                  onChange={(e) => handlePropChange("bgImage", e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.overlayOpacity !== undefined && (
              <div>
                <div className="flex justify-between text-[11px] text-gray-500 mb-1">
                  <span>Overlay Darkening</span>
                  <span>{props.overlayOpacity}%</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={props.overlayOpacity}
                  onChange={(e) => handlePropChange("overlayOpacity", parseInt(e.target.value, 10))}
                  className="w-full accent-indigo-600"
                />
              </div>
            )}
          </div>
        )}

        {/* SPACING & LAYOUT */}
        {(props.padding !== undefined || props.borderRadius !== undefined || props.gap !== undefined) && (
          <div className="space-y-3 pt-3 border-t border-gray-100 dark:border-gray-800">
            <label className="flex items-center gap-1.5 font-bold text-gray-900 dark:text-gray-100 uppercase text-[10px] tracking-wider">
              <MdLayers size={14} className="text-indigo-500" />
              <span>Spacing & Layout</span>
            </label>

            {props.padding !== undefined && (
              <div>
                <div className="flex justify-between text-[11px] text-gray-500 mb-1">
                  <span>Padding</span>
                  <span>{props.padding}px</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={80}
                  value={props.padding}
                  onChange={(e) => handlePropChange("padding", parseInt(e.target.value, 10))}
                  className="w-full accent-indigo-600"
                />
              </div>
            )}

            {props.borderRadius !== undefined && (
              <div>
                <div className="flex justify-between text-[11px] text-gray-500 mb-1">
                  <span>Corner Radius</span>
                  <span>{props.borderRadius}px</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={32}
                  value={props.borderRadius}
                  onChange={(e) => handlePropChange("borderRadius", parseInt(e.target.value, 10))}
                  className="w-full accent-indigo-600"
                />
              </div>
            )}
          </div>
        )}

        {/* DIVIDER / SPACER CONTROLS */}
        {props.height !== undefined && (
          <div className="space-y-3 pt-3 border-t border-gray-100 dark:border-gray-800">
            <label className="flex items-center gap-1.5 font-bold text-gray-900 dark:text-gray-100 uppercase text-[10px] tracking-wider">
              <MdLayers size={14} className="text-indigo-500" />
              <span>Divider & Spacing</span>
            </label>

            <div>
              <div className="flex justify-between text-[11px] text-gray-500 mb-1">
                <span>Height / Spacing</span>
                <span>{props.height}px</span>
              </div>
              <input
                type="range"
                min={8}
                max={120}
                value={props.height}
                onChange={(e) => handlePropChange("height", parseInt(e.target.value, 10))}
                className="w-full accent-indigo-600"
              />
            </div>

            {props.showLine !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-gray-500">Show Separator Line</span>
                <input
                  type="checkbox"
                  checked={props.showLine}
                  onChange={(e) => handlePropChange("showLine", e.target.checked)}
                  className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                />
              </div>
            )}

            {props.lineColor !== undefined && (
              <div>
                <span className="text-[11px] text-gray-500 mb-1 block">Line Color</span>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={props.lineColor}
                    onChange={(e) => handlePropChange("lineColor", e.target.value)}
                    className="w-7 h-7 rounded border border-gray-300 cursor-pointer"
                  />
                  <span className="font-mono text-[11px]">{props.lineColor}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
